/**
 * Client-side image compression to keep upload payloads small enough
 * to pass any production ingress/body-size limits (target <800KB base64).
 *
 * Iteratively re-encodes an image, dropping dimensions and quality until the
 * resulting data URL is under `targetBytes`. Non-image files (PDFs) are
 * accepted only if under `pdfMaxBytes`; otherwise an Error is thrown.
 */

const DEFAULT_TARGET = 800 * 1024;    // ~800KB after base64
const DEFAULT_PDF_LIMIT = 2 * 1024 * 1024; // 2MB raw PDF

export async function fileToCompressedDataUrl(
  file,
  {
    targetBytes = DEFAULT_TARGET,
    pdfMaxBytes = DEFAULT_PDF_LIMIT,
    startMaxDim = 1600,
    minMaxDim = 640,
    startQuality = 0.85,
    minQuality = 0.4,
  } = {}
) {
  if (!file) return '';

  if (!file.type.startsWith('image/')) {
    if (file.size > pdfMaxBytes) {
      throw new Error(
        `File is ${(file.size / 1024 / 1024).toFixed(1)}MB. PDFs must be under ${(pdfMaxBytes / 1024 / 1024).toFixed(0)}MB.`
      );
    }
    return await readAsDataUrl(file);
  }

  const originalDataUrl = await readAsDataUrl(file);
  const img = await loadImage(originalDataUrl);

  // Try progressively smaller dimensions and lower quality until under target
  const attempts = [
    [startMaxDim, startQuality],
    [1280, 0.8],
    [1024, 0.75],
    [900, 0.7],
    [800, 0.65],
    [minMaxDim, 0.6],
    [minMaxDim, minQuality],
  ];

  let last = '';
  for (const [maxDim, quality] of attempts) {
    const out = encode(img, maxDim, quality);
    last = out;
    if (dataUrlSizeBytes(out) <= targetBytes) return out;
  }
  return last; // best effort — smallest we managed
}

function encode(img, maxDim, quality) {
  const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, w, h);
  return canvas.toDataURL('image/jpeg', quality);
}

function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onloadend = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

export function dataUrlSizeBytes(dataUrl) {
  if (!dataUrl) return 0;
  const commaIdx = dataUrl.indexOf(',');
  const b64 = commaIdx >= 0 ? dataUrl.slice(commaIdx + 1) : dataUrl;
  return Math.floor((b64.length * 3) / 4);
}
